import { useTranslation } from 'react-i18next'
import { useDialog, usePolicy } from '../hooks'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { Generator } from '../types'
import { Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useEffect, useState } from 'react'
import { updateGenerator } from '../services/generator'
import { useDispatch } from 'react-redux'
import { createSuccessNotification } from '../redux/slices/notifications'
import RentalCompanyAutocomplete from './RentalCompanyAutocomplete'

export interface GeneratorCompanyEditButtonProps extends Omit<
  IconButtonProps,
  'children' | 'onClick' | 'onChange'
> {
  generator: Generator
  onGeneratorChange?: (generator: Generator) => void
}

export default function GeneratorCompanyEditButton({
  generator,
  onGeneratorChange,
  ...props
}: GeneratorCompanyEditButtonProps) {
  const { t } = useTranslation('generator')

  const dispatch = useDispatch()

  const { canEditGenerator } = usePolicy()

  const { open, openDialog, closeDialog } = useDialog()

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(generator.companyId)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [submitError, setSubmitError] = useState<unknown>(null)

  /**
   * Reset submitting state when dialog is closed.
   */
  useEffect(() => {
    if (!open) {
      setSubmitting(false)
      setSubmitError(null)
    }
  }, [open])

  if (!canEditGenerator(generator)) {
    return null
  }

  async function handleSubmit() {
    try {
      setSubmitting(true)

      const updatedGenerator = await updateGenerator(generator.id, {
        company: selectedCompanyId
      })

      dispatch(createSuccessNotification(t('powerSourceUpdateSuccess')))

      onGeneratorChange?.(updatedGenerator)

      closeDialog()
    } catch (error) {
      setSubmitError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Tooltip title={t('edit')}>
        <IconButton {...props} onClick={openDialog}>
          <Edit color="action" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('selectCompany')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <RentalCompanyAutocomplete
              value={selectedCompanyId}
              onChange={(_, value) => setSelectedCompanyId(value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" disabled={isSubmitting} onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <LoadingButton
            variant="contained"
            color="primary"
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {t('saveChanges')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
